import React from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Logo } from "../components/Logo";
import { ShieldCheck, Calendar, AlertTriangle, ArrowRight, Copy } from "lucide-react";
import { toast } from "sonner";

const PERFIS = [
  { label: "Diretor",     email: "diretor@locarativosbrasil.com.br",     color: "bg-red-50 border-red-200 text-red-700", desc: "Acesso total ao sistema" },
  { label: "Financeiro",  email: "financeiro@locarativosbrasil.com.br",  color: "bg-emerald-50 border-emerald-200 text-emerald-700", desc: "Cobranças e relatórios financeiros" },
  { label: "Vendedor",    email: "vendedor@locarativosbrasil.com.br",    color: "bg-blue-50 border-blue-200 text-blue-700", desc: "Clientes, propostas e comercial" },
  { label: "Operador",    email: "operador@locarativosbrasil.com.br",    color: "bg-cyan-50 border-cyan-200 text-cyan-700", desc: "Frota e contratos operacionais" },
  { label: "Oficina",     email: "oficina@locarativosbrasil.com.br",     color: "bg-amber-50 border-amber-200 text-amber-700", desc: "Ordens de serviço e manutenção" },
  { label: "Supervisão",  email: "supervisao@locarativosbrasil.com.br",  color: "bg-purple-50 border-purple-200 text-purple-700", desc: "Visão ampla (leitura)" },
  { label: "Cliente",     email: "cliente@locarativosbrasil.com.br",     color: "bg-zinc-50 border-zinc-200 text-zinc-700", desc: "Portal externo (acesso restrito)" },
];

function calcExpira() {
  const d = new Date();
  d.setDate(d.getDate() + 60);
  return d.toLocaleDateString("pt-BR");
}

const copy = (txt) => {
  navigator.clipboard?.writeText(txt).then(() => toast.success("Copiado!"));
};

export default function BemVindo() {
  const expira = calcExpira();
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 px-4 py-12">
      <div className="max-w-4xl mx-auto" data-testid="page-bem-vindo">
        <div className="text-center mb-10">
          <Logo size="lg" />
          <div className="mt-2 text-xs uppercase tracking-[0.3em] text-zinc-500">Ambiente de Testes</div>
          <h1 className="mt-6 text-3xl sm:text-4xl font-display font-bold text-zinc-900">
            Bem-vindo(a) à plataforma LAB
          </h1>
          <p className="mt-3 text-sm text-zinc-600 max-w-xl mx-auto">
            Esta é a área de testes da Locar Ativos Brasil. Use as contas abaixo para explorar cada perfil.
          </p>
        </div>

        <Card className="p-6 mb-8 border-orange-200 bg-orange-50/40">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
            <div className="text-sm text-zinc-700">
              <strong>Ambiente de testes</strong> — não inserir dados reais de clientes ou contratos.
              <br />
              Acessos válidos até <strong className="text-orange-700">{expira}</strong> (60 dias a partir de hoje).
            </div>
          </div>
        </Card>

        <Card className="p-6 mb-8">
          <h2 className="text-base font-semibold mb-1 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-orange-600" /> Senha padrão
          </h2>
          <div className="flex items-center gap-3">
            <code className="text-2xl font-mono font-bold text-zinc-900 bg-zinc-100 px-4 py-2 rounded">lab123</code>
            <Button variant="outline" size="sm" onClick={() => copy("lab123")} data-testid="copy-senha">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Use a mesma senha em todos os perfis abaixo.</p>
        </Card>

        <h2 className="text-base font-semibold mb-3 text-zinc-700 uppercase tracking-wide">
          Perfis disponíveis para teste
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {PERFIS.map((p) => (
            <Card key={p.email} className={`p-4 border ${p.color}`} data-testid={`perfil-${p.label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm">{p.label}</div>
                  <button
                    type="button"
                    onClick={() => copy(p.email)}
                    className="text-xs font-mono hover:underline truncate block w-full text-left mt-0.5"
                    title="Copiar e-mail"
                  >
                    {p.email}
                  </button>
                  <div className="text-[11px] text-zinc-500 mt-1">{p.desc}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => copy(p.email)} className="shrink-0">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <Card className="p-6 mb-6 bg-zinc-900 text-white border-zinc-900">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/60 mb-1">URL do sistema</div>
              <div className="font-mono text-sm text-orange-300">locarativosbrasil.com.br</div>
            </div>
            <Link to="/login">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="ir-para-login">
                Ir para o login <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </Card>

        <div className="text-center text-xs text-zinc-400 flex items-center justify-center gap-2">
          <Calendar className="w-3 h-3" />
          Validade dos acessos: <strong className="text-zinc-600">{expira}</strong>
        </div>
      </div>
    </div>
  );
}
